import { useCallback, useState } from 'react'
import { useStorage } from '../providers/platform-provider'
import type { PDFOptions } from '../types/cv'

interface UsePDFExportReturn {
  isGenerating: boolean
  error: Error | null
  generatePDF: (cvId: string, options?: PDFOptions) => Promise<Blob | null>
  downloadPDF: (cvId: string, filename?: string, options?: PDFOptions) => Promise<void>
}

/**
 * Hook for PDF generation and download
 */
export function usePDFExport(): UsePDFExportReturn {
  const storage = useStorage()
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const generatePDF = useCallback(
    async (cvId: string, options?: PDFOptions): Promise<Blob | null> => {
      setIsGenerating(true)
      setError(null)
      try {
        const blob = await storage.generatePDF(cvId, options)
        return blob
      } catch (e) {
        const err = e instanceof Error ? e : new Error('Failed to generate PDF')
        setError(err)
        return null
      } finally {
        setIsGenerating(false)
      }
    },
    [storage]
  )

  const downloadPDF = useCallback(
    async (cvId: string, filename = 'cv.pdf', options?: PDFOptions) => {
      const blob = await generatePDF(cvId, options)
      if (!blob) return

      // Create download link
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },
    [generatePDF]
  )

  return {
    isGenerating,
    error,
    generatePDF,
    downloadPDF,
  }
}
